import { ESIInclude } from "@/components/__shared__/ESIInclude/ESIInclude";
import classNames from "classnames/bind";
import styles from "./Wishlist.module.scss";
import { WishlistConfirmation } from "./WishlistConfirmation/WishlistConfirmation";
import { useEffect, useState } from "react";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { dispatchCustomEvent } from "@/utils/customEvent/dispatchCustomEvent.ts";
import { BaseButtonState } from "@/components/AddToBasket/AddToBasketControls/hooks/useButtonStates.ts";

const cx = classNames.bind(styles);

type Props = {
  productId: string;
  skuId: string;
  variantId: string;
  isSelected: boolean;
  isVariantAvailable: boolean;
  showWishlistIconButtonVariant: boolean;
  addToBasketButtonState: BaseButtonState;
  expressButtonState: BaseButtonState;
  accountBaseUrl: string;
};

export const Wishlist = ({
  showWishlistIconButtonVariant,
  productId,
  skuId,
  variantId,
  isSelected,
  isVariantAvailable,
  addToBasketButtonState,
  expressButtonState,
  accountBaseUrl,
}: Props) => {
  const [addedToWishlist, setAddedToWishlist] = useState(false);
  const [wishlistError, setWishlistError] = useState(false);
  const [addedToMultipleWishlists, setAddedToMultipleWishlists] =
    useState(false);

  const wishlistVariantChange = () => {
    setAddedToWishlist(false);
    setAddedToMultipleWishlists(false);
    dispatchCustomEvent("ADD_TO_BASKET_ESI:VARIANT_CHANGE_FOR_WISHLIST");
  };

  const showWishlistConfirmation = (isMultipleWishlists: boolean) => {
    setWishlistError(false);
    setAddedToMultipleWishlists(isMultipleWishlists);
    setAddedToWishlist(true);
  };

  const showWishlistError = () => {
    setAddedToWishlist(false);

    setWishlistError(true);
  };

  const removeWishlistConfirmation = () => {
    setAddedToWishlist(false);
  };

  useEffect(() => {
    const onWishlistSuccess: EventListener = (event) => {
      let isMultipleWishlists = false;

      if (event instanceof CustomEvent && event.detail) {
        const detail = event.detail;

        if (typeof detail === "object" && "isMultipleWishlists" in detail) {
          isMultipleWishlists = Boolean(detail.isMultipleWishlists);
        }
      }

      showWishlistConfirmation(isMultipleWishlists);
    };

    const onWishlistError: EventListener = () => {
      showWishlistError();
    };

    const onWishlistRemoveSuccess: EventListener = () => {
      removeWishlistConfirmation();
    };

    window.addEventListener("ADD_TO_WISHLIST_ESI:SUCCESS", onWishlistSuccess);
    window.addEventListener("ADD_TO_WISHLIST_ESI:ERROR", onWishlistError);
    window.addEventListener(
      "ADD_TO_WISHLIST_ESI:REMOVE_SUCCESS",
      onWishlistRemoveSuccess,
    );

    return () => {
      window.removeEventListener(
        "ADD_TO_WISHLIST_ESI:SUCCESS",
        onWishlistSuccess,
      );
      window.removeEventListener("ADD_TO_WISHLIST_ESI:ERROR", onWishlistError);
      window.removeEventListener(
        "ADD_TO_WISHLIST_ESI:REMOVE_SUCCESS",
        onWishlistRemoveSuccess,
      );
    };
  }, []);

  useEffect(() => {
    wishlistVariantChange();
  }, [skuId, isSelected]);

  const anyButtonVisible =
    addToBasketButtonState.visible || expressButtonState.visible;

  if (!showWishlistIconButtonVariant) {
    return null;
  }

  const shouldShowFullWidthWishlist = !anyButtonVisible;

  const productDetails = JSON.stringify({
    productId,
    isOutOfStock: isVariantAvailable,
  });

  const wishlistButton = (isOosButton: boolean) => (
    <div
      data-testid="wish-list-esi-button"
      data-wishlist-placeholder={true}
      data-product-id={productId}
      data-product-skuid={skuId}
      data-product-variantid={isSelected ? variantId : ""}
      data-product-multi-sku={true}
      data-product-oos-button={isOosButton}
      className={isOosButton ? cx("oosWishlistButton") : undefined}
    />
  );

  return (
    <>
      <span
        id="add-to-wish-list-esi"
        data-test-id="wish-list-esi"
        data-mode="icon-placeholder-pdp"
        data-product-details={productDetails}
        className={cx("hidden")}
      >
        <ESIInclude
          id="add-to-wishlist-component"
          src="/add-to-wishlist/exclude-cpa.html"
        />
      </span>

      <div
        data-testid="add-to-wish-list-wrapper-full-width"
        className={cx("wishlistWrapper", {
          fullWidth: shouldShowFullWidthWishlist,
          hiddenInSmallScreen:
            showWishlistIconButtonVariant && !shouldShowFullWidthWishlist,
        })}
      >
        {wishlistButton(true)}
      </div>

      <div
        data-testid="add-to-wish-list-wrapper-icon-button"
        className={cx("wishlistWrapper", {
          hidden: shouldShowFullWidthWishlist,
          hiddenInLargeScreen: showWishlistIconButtonVariant,
        })}
      >
        {wishlistButton(false)}
      </div>
      <WishlistConfirmation
        addedToWishlist={addedToWishlist}
        addedToMultipleWishlists={addedToMultipleWishlists}
        variantId={variantId}
        accountBaseUrl={accountBaseUrl}
      />
      {wishlistError && (
        <DEPRECATED_Message
          className={cx("errorMessage", styles.fullWidth)}
          title="There was a problem with wish list. Please refresh and try again."
          type="error"
          dataTestId={`wishlist-error-message`}
        />
      )}
    </>
  );
};
